import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../api/client';
import type { Me, UpdateMeBody } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { setAppLanguage } from '../i18n';

interface UserContextValue {
  me: Me | null;
  isLoading: boolean;
  error: string | null;
  /** Re-fetch /api/me from scratch. Flips `isLoading`, which unmounts the route tree behind
   * RequireTraditions' full-page spinner -- fine for the initial load, but calling this from
   * inside an already-mounted page (e.g. polling after a billing checkout) would flash a
   * spinner and reset that page's own local state. Use refreshQuiet for that instead. */
  refresh: () => Promise<void>;
  /** Same re-fetch, without touching `isLoading` -- safe to call from within an already-mounted
   * page without unmounting it. Returns the freshly-fetched profile so a caller polling for a
   * specific change (e.g. tier flipping after a billing webhook) doesn't need a second fetch
   * just to inspect the result it already has. */
  refreshQuiet: () => Promise<Me | null>;
  /** PUT /api/me and merge the (server-derived) response into local state. */
  updateMe: (body: UpdateMeBody) => Promise<Me>;
  /** Apply a session result (from postSession) into the cached `me` without a round trip. */
  applySessionResult: (
    patch: Pick<
      Me,
      | 'currentStreak'
      | 'longestStreak'
      | 'lastPracticeDate'
      | 'completedPracticeIds'
      | 'shieldsAvailable'
      | 'pilgrimRank'
      | 'totalPracticeDays'
      | 'practiceMilestone'
    >,
  ) => void;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [me, setMe] = useState<Me | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshQuiet = useCallback(async () => {
    try {
      const result = await api.getMe();
      setMe(result);
      setError(null);
      return result;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load profile');
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      await refreshQuiet();
    } finally {
      setIsLoading(false);
    }
  }, [refreshQuiet]);

  useEffect(() => {
    if (isAuthenticated) {
      void refresh();
    } else {
      setMe(null);
      setIsLoading(false);
    }
  }, [isAuthenticated, refresh]);

  // Syncs the server-stored language preference to a device/browser that hasn't picked one
  // locally yet (e.g. a fresh sign-in) -- a no-op if it already matches what Settings just set,
  // since setAppLanguage just re-applies the same value in that case.
  useEffect(() => {
    if (me?.locale) setAppLanguage(me.locale);
  }, [me?.locale]);

  const updateMe = useCallback(async (body: UpdateMeBody) => {
    const result = await api.updateMe(body);
    setMe(result);
    return result;
  }, []);

  const applySessionResult = useCallback(
    (
      patch: Pick<
        Me,
        | 'currentStreak'
        | 'longestStreak'
        | 'lastPracticeDate'
        | 'completedPracticeIds'
        | 'shieldsAvailable'
        | 'pilgrimRank'
        | 'totalPracticeDays'
        | 'practiceMilestone'
      >,
    ) => {
      setMe((prev) => (prev ? { ...prev, ...patch } : prev));
    },
    [],
  );

  const value = useMemo<UserContextValue>(
    () => ({ me, isLoading, error, refresh, refreshQuiet, updateMe, applySessionResult }),
    [me, isLoading, error, refresh, refreshQuiet, updateMe, applySessionResult],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return ctx;
}
